import { Avatar, Box, Button, ButtonProps, SxProps } from "@mui/material";
import { Theme } from "@mui/material/styles";
import { CSSProperties } from "react";

import { getInitials, initialsAvatarSx } from "@/pages/utils/avatarDisplay";

type ProductButtonProps = {
  drawerOpen: boolean;
  dropdownOpen: boolean;
  productName: string;
  productIconLink: string;
  disableSelect?: boolean;
} & ButtonProps;

export default function ProductButton({
  drawerOpen,
  dropdownOpen,
  productName,
  productIconLink,
  disableSelect,
  onClick,
  onMouseOver,
  ...props
}: ProductButtonProps) {
  const drawerOpenStyles: SxProps<Theme> = {
    fontSize: "16px",
    padding: "11px 16px 11px 18px",
    justifyContent: "flex-start",
    gap: "10px",
    width: "100%",
  };

  const drawerClosedStyles: SxProps<Theme> = {
    justifyContent: "center",
    alignItems: "center",
    padding: 0,
    minWidth: "60px",
  };

  const styles = drawerOpen ? drawerOpenStyles : drawerClosedStyles;

  const drawerOpenIconStyles: CSSProperties = {
    width: "20px",
    height: "20px",
  };

  const drawerClosedIconStyles: CSSProperties = {
    width: "60px",
    height: "60px",
    maxWidth: "60px",
    maxHeight: "60px",
    minWidth: "60px",
  };

  const iconStyles = drawerOpen ? drawerOpenIconStyles : drawerClosedIconStyles;

  // Selection is disabled for now, but we can add it back in the future
  const buttonConditionalProps: ButtonProps = !disableSelect
    ? {
        "aria-controls": dropdownOpen ? "basic-menu" : undefined,
        "aria-haspopup": "true",
        "aria-expanded": dropdownOpen ? "true" : undefined,
        onClick,
        onMouseOver,
        sx: {},
      }
    : {
        sx: {
          cursor: "default",
          "&:hover": {
            backgroundColor: "inherit",
            color: "inherit",
          },
          "&:focus-visible": {
            outline: "none",
          },
        },
      };

  return (
    <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
      <Button
        {...props}
        {...buttonConditionalProps}
        sx={{
          fontWeight: 700,
          lineHeight: "22px",
          color: "#202020",
          "& .MuiButton-startIcon": {
            mx: 0,
            // The Button override forces start-icon children to font-size: inherit,
            // so drive the avatar's initials font from here (proportional to size).
            fontSize: drawerOpen ? "8px" : "24px",
          },
          ...styles,
          ...buttonConditionalProps.sx,
        }}
        startIcon={
          <Avatar
            src={productIconLink}
            alt="avatar"
            variant="rounded"
            sx={{ ...iconStyles, borderRadius: "6px", ...initialsAvatarSx(drawerOpen ? 20 : 60) }}
          >
            {getInitials(productName)}
          </Avatar>
        }
      >
        {drawerOpen ? productName : ""}
      </Button>
    </Box>
  );
}
